import { useEffect, useRef, useState } from 'react'
import type { SpeakResult } from '../api'
import { describeHolder, type HeldBy } from '../useSpeaker'

// The voice service's limit; the server refuses anything longer.
const MAX_CHARACTERS = 500

type Props = {
  siteName: string
  camera: string
  speak: (text: string, signal: AbortSignal) => Promise<SpeakResult>
  onClose: () => void
  // Someone else holding the camera's speaker; the server would refuse.
  heldBy?: HeldBy | null
  phrases: string[]
}


// The camera card's look for each state of its speaker.
const speakerStates = {
  free: { word: 'Free', text: 'text-live', dot: 'bg-live', border: 'border-zinc-800', icon: 'bg-orange-500/15 text-orange-300' },
  speaking: { word: 'Speaking', text: 'text-orange-300', dot: 'bg-orange-400', border: 'border-orange-400/60', icon: 'bg-orange-400 text-zinc-950' },
  inUse: { word: 'In use', text: 'text-attention', dot: 'bg-attention', border: 'border-attention/40', icon: 'bg-zinc-800 text-zinc-400' },
}

export function SpeakPanel({ siteName, camera, speak, onClose, heldBy = null, phrases }: Props) {
  const [text, setText] = useState('')
  const [playing, setPlaying] = useState<AbortController | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [missed, setMissed] = useState<string[]>([])
  // A closed panel has no Stop, so going away stops the message rather than
  // leaving the camera talking with nobody able to cut it off.
  const inFlight = useRef<AbortController | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => () => inFlight.current?.abort(), [])

  const send = async () => {
    if (!text.trim() || playing || heldBy) return
    const controller = new AbortController()
    inFlight.current = controller
    setPlaying(controller)
    setError(null)
    setMissed([])
    const result = await speak(text, controller.signal)
    inFlight.current = null
    setPlaying(null)
    if (result.outcome === 'played') {
      setText('')
      setMissed(result.missed ?? [])
    }
    if (result.outcome === 'failed') setError(result.reason)
  }

  const speaker = speakerStates[playing ? 'speaking' : heldBy ? 'inUse' : 'free']

  return (
    <aside
      className="w-[23.75rem] shrink-0 flex flex-col bg-zinc-900 border-l border-zinc-800 relative overflow-y-auto scrollbar-thin"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
        // Anywhere in the panel, not only in the box: with focus on a phrase
        // or on Clear, it did nothing (#287).
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault()
          void send()
        }
      }}
    >
      <div className="shrink-0 flex items-center justify-between px-[1.125rem] pt-[1.125rem] pb-3">
        <span className="text-base font-semibold text-zinc-100">Speak</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className={`shrink-0 mx-[1.125rem] rounded-lg bg-zinc-950 border ${speaker.border}`}>
        <div className="flex items-center gap-3 px-3 py-3">
          <div className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-md ${speaker.icon}`}>
            <SpeakerIcon />
          </div>
          <h2 className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-semibold text-zinc-100 truncate">{camera}</span>
            <span className="text-[13px] font-normal text-zinc-400 truncate">{siteName}</span>
          </h2>
          <span className={`flex items-center gap-1.5 text-[13px] font-semibold ${speaker.text}`}>
            <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${speaker.dot}`} />
            {speaker.word}
          </span>
        </div>
        {heldBy && (
          <p role="status" className={`px-3 py-2 border-t ${speakerStates.inUse.border} bg-attention/10 rounded-b-lg text-[13px] text-attention`}>
            {describeHolder(heldBy)}
          </p>
        )}
      </div>

      {phrases.length > 0 && (
        <div className="shrink-0 flex flex-col gap-1.5 px-[1.125rem] pt-4">
          <span className="text-[13px] text-zinc-400">Phrases</span>
          {phrases.map((phrase, i) => (
            <button
              key={phrase}
              type="button"
              onClick={() => {
                setText(phrase)
                // Into the box, where the phrase can be adapted and sent.
                box.current?.focus()
              }}
              disabled={playing !== null}
              className="min-h-10 flex items-center gap-2.5 px-3 py-2 rounded-md border border-zinc-800 text-left text-sm text-zinc-100 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span aria-hidden="true" className="w-4 shrink-0 font-mono text-xs text-zinc-400 tabular-nums">
                {i + 1}
              </span>
              {phrase}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1.5 px-[1.125rem] pt-4 flex-1">
        <div className="flex items-baseline justify-between">
          <label htmlFor="speak-message" className="text-[13px] text-zinc-400">
            Message
          </label>
          <span className="font-mono text-[13px] text-zinc-400 tabular-nums">
            {text.length} / {MAX_CHARACTERS}
          </span>
        </div>
        {/* On a phone or tablet the panel opens out of sight under the camera;
            focus brings it into view, and the box is what Speak was pressed for. */}
        <textarea
          ref={box}
          autoFocus
          id="speak-message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_CHARACTERS}
          readOnly={playing !== null}
          className="flex-1 min-h-[120px] w-full resize-none rounded-md bg-zinc-950 border border-zinc-500 px-3 py-2.5 text-sm max-sm:text-base leading-relaxed text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400 read-only:text-zinc-300"
          placeholder="Type what the camera should say, or pick a phrase"
        />
      </div>

      {/* Held at the panel's foot, so Send and what it last said are in view
          however short the window: the phrases and the box scroll above it. */}
      <div className="sticky bottom-0 z-10 shrink-0 flex flex-col gap-2.5 mt-4 px-[1.125rem] pt-3.5 pb-4 border-t border-zinc-800 bg-zinc-900">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setText('')}
            disabled={!text || playing !== null}
            className="h-8 max-sm:h-11 px-2.5 flex items-center gap-1.5 rounded-md border border-zinc-700 text-[13px] font-semibold text-zinc-200 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            Clear
          </button>
          <span className="flex items-center gap-1 text-xs text-zinc-400">
            <kbd className="px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800 font-mono text-[11px] text-zinc-300">Ctrl</kbd>
            <span aria-hidden="true">+</span>
            <kbd className="px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800 font-mono text-[11px] text-zinc-300">Enter</kbd>
            <span className="ml-0.5">sends</span>
          </span>
        </div>
        {/* Just above Send, where the dispatcher is looking once it is pressed:
            in the message area it slid under this footer (#287). Said plainly
            first, with the server's reason — often go2rtc's own words — beneath. */}
        {error && (
          <div role="alert" className="px-3 py-2.5 flex flex-col gap-1 rounded-md bg-fault/10 border border-fault/40">
            <p className="text-sm font-semibold text-fault">The message didn’t play through {camera}.</p>
            <p className="text-xs leading-relaxed text-zinc-300 break-words">{error}</p>
          </div>
        )}
        {missed.length > 0 && (
          <p role="status" className="px-2.5 py-2 rounded-md bg-attention/10 text-[13px] leading-relaxed text-attention">
            Played, but {missed.join(' and ')} did not answer.
          </p>
        )}
        {playing ? (
          <button
            type="button"
            onClick={() => playing.abort()}
            className="h-11 rounded-md text-sm font-bold bg-fault text-zinc-950 hover:brightness-110"
          >
            Stop
          </button>
        ) : heldBy ? (
          <button
            type="button"
            disabled
            className="h-11 rounded-md text-sm font-semibold bg-zinc-800 border border-zinc-700 text-zinc-400 cursor-not-allowed"
          >
            Wait for {heldBy.name} to finish
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void send()}
            disabled={!text.trim()}
            className="h-11 flex items-center justify-center gap-2 rounded-md text-sm font-bold bg-orange-400 text-zinc-950 hover:bg-orange-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <SendIcon />
            {error ? 'Send again' : `Send to ${camera}`}
          </button>
        )}
      </div>
    </aside>
  )
}

function SpeakerIcon() {
  return (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z"
      />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  )
}
